import { FloatingPanelCollisionStrategy } from './types';
import { RootState, Size } from '@react-three/fiber';
import * as THREE from 'three';

/**
 * An optional enum that allows you to specify how collisions should be resolved:
 * - XY: Resolve in both axes (default).
 * - X: Resolve only on the horizontal (x) axis.
 * - Y: Resolve only on the vertical (y) axis.
 */
export enum CollisionResolutionAxis {
  XY = 0b11,
  X = 0b10,
  Y = 0b01,
}

export class Node {
  position: THREE.Vector2Tuple = [0, 0];
  previousPosition: THREE.Vector2Tuple = [0, 0];
  acceleration: THREE.Vector2Tuple = [0, 0];
  applyForce(force: THREE.Vector2Tuple) {
    this.acceleration[0] += force[0];
    this.acceleration[1] += force[1];
  }
  update(delta: number, damping = 0.95) {
    const vx = (this.position[0] - this.previousPosition[0]) * damping;
    const vy = (this.position[1] - this.previousPosition[1]) * damping;

    // Verlet integration update.
    const nextX = this.position[0] + vx + this.acceleration[0] * delta * delta;
    const nextY = this.position[1] + vy + this.acceleration[1] * delta * delta;

    // Update the previous and current positions.
    this.previousPosition[0] = this.position[0];
    this.previousPosition[1] = this.position[1];
    this.position[0] = nextX;
    this.position[1] = nextY;

    // Reset acceleration.
    this.acceleration[0] = 0;
    this.acceleration[1] = 0;
  }
}
export interface ExtendedNodeOptions {
  margin?: number;
  offset?: THREE.Vector2Tuple;
  anchor?: THREE.Vector2Tuple;
}

export class ExtendedNode extends Node {
  lastUpdate: number = 0;
  margin: number = 0;
  offset: THREE.Vector2Tuple = [0, -200];
  anchor: THREE.Vector2Tuple = [0.5, 0.5];
  constructor(
    public id: string,
    public element: HTMLElement,
    options?: ExtendedNodeOptions,
  ) {
    super();
    this.margin = options?.margin ?? this.margin;
    this.offset = options?.offset ?? this.offset;
    this.anchor = options?.anchor ?? this.anchor;
  }
  intersect(other: ExtendedNode): boolean {
    const rectA = this.getSize();
    const rectB = other.getSize();

    return (
      rectA.left < rectB.right &&
      rectA.right > rectB.left &&
      rectA.top < rectB.bottom &&
      rectA.bottom > rectB.top
    );
  }

  getSize(): Size & { right: number; bottom: number } {
    const rect = this.element.getBoundingClientRect();
    const halfMargin = this.margin / 2;
    const size = {
      width: rect.width + this.margin,
      height: rect.height + this.margin,
      top: rect.top - halfMargin,
      left: rect.left - halfMargin,
      right: rect.right + halfMargin,
      bottom: rect.bottom + halfMargin,
    };

    return size;
  }

  getCenter(): THREE.Vector2Tuple {
    const rect = this.getSize();
    return [rect.left + rect.width / 2, rect.top + rect.height / 2];
  }
}

export class ForceLayoutCollisionStrategy
  extends Map<string, ExtendedNode>
  implements FloatingPanelCollisionStrategy<ExtendedNode, ExtendedNodeOptions>
{
  private readonly damping = 0.8;

  step(_: RootState, delta: number) {
    this.applyForces();
    this.update(delta);
    return true;
  }

  private applyForces() {
    this.forEach((node) => {
      // Brute-force collision resolution
      const nodeRect = node.getSize();
      const nodeCenter = node.getCenter();
      let collision = false;
      this.forEach((other) => {
        if (other.id !== node.id) {
          if (node.intersect(other)) {
            collision = true;
            const otherRect = other.getSize();
            const otherCenter = other.getCenter();

            const dx = nodeCenter[0] - otherCenter[0];
            const dy = nodeCenter[1] - otherCenter[1];
            const distance = Math.sqrt(dx * dx + dy * dy) || 0.0001;

            const overlapX = (nodeRect.width + otherRect.width) / 2 - Math.abs(dx);
            const overlapY = (nodeRect.height + otherRect.height) / 2 - Math.abs(dy);

            const forceMagnitude = Math.min(overlapX, overlapY);
            const forceDir = [dx / distance, dy / distance];

            const repulsion: THREE.Vector2Tuple = [
              0, // forceDir[0] * forceMagnitude * 10,
              Math.min(0, forceDir[1]) * forceMagnitude * 20 * 20,
            ];

            node.applyForce(repulsion);
          }
        }
      });

      const dx = node.offset[0] - node.position[0];
      const dy = node.offset[1] - node.position[1];

      const attractionStrength = collision ? 10 : 20 * 10;
      const attractionForce: THREE.Vector2Tuple = [
        dx * attractionStrength,
        dy * attractionStrength,
      ];

      node.applyForce(attractionForce);
    });
  }

  private update(delta: number) {
    this.forEach((node) => {
      const mDelta = Math.min(delta, 1 / 60) / 4;
      for (let i = 0; i < 4; i++) {
        node.update(mDelta, this.damping);
      }

      node.element.style.transform = `translate(${node.position[0]}px, ${node.position[1]}px)`;
    });
  }

  add(id: string, element: HTMLElement, options?: ExtendedNodeOptions): this {
    super.set(id, new ExtendedNode(id, element, options));
    return this;
  }
}
